import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Styles from './grouppolicyStyle.module.scss';
import Project from '../../common/Utils/project';
import { useAuth } from 'oidc-react';
import { usePolicyAutomation } from '../../providers/GroupPolicyAutomationProvider';
import { LoaderCircle } from 'lucide-react';
import Pagination from '../../components/Pagination/Pagination';
import { useTenantSelection } from '../../providers/TenantSelectionProvider';
import api from '../../components/services/apiService';
import { usePermissions } from '../../hooks/usePermissions';

type SoftwareItem = {
  id: number;
  packageIdentifier: string;
  packageName: string;
  version: number;
  isUpdateonly: boolean;
  isInstallonly: boolean;
};

type SelectedPackage = {
  packageIdentifier: string;
  version: string;
  isUpdateonly: boolean;
  isInstallonly: boolean;
};

type PaginationInfo = {
  totalPages: number;
};

const ITEMS_PER_PAGE = 20;
const DEFATOM_AGENT_IDENTIFIER = 'defatom.agent';

const isDefatomAgent = (packageIdentifier: string) => packageIdentifier?.toLowerCase() === DEFATOM_AGENT_IDENTIFIER;

const ThirdPartyForm = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [data, setData] = useState<SoftwareItem[]>([]);
  const { PolicyAutomationData, setPolicyAutomation } = usePolicyAutomation();
  const [selectedPackages, setSelectedPackages] = useState<SelectedPackage[]>(() => {
    const initialPackages = (PolicyAutomationData.policyContent?.thirdPartyUpdates?.packages as SelectedPackage[] | undefined) ?? [];
    const activePackages = initialPackages.filter((pkg) => pkg.isUpdateonly || pkg.isInstallonly);
    const hasNonAgentSelected = activePackages.some((pkg) => !isDefatomAgent(pkg.packageIdentifier));

    if (hasNonAgentSelected) {
      return activePackages.filter((pkg) => !isDefatomAgent(pkg.packageIdentifier));
    }

    const selectedAgent = activePackages.find((pkg) => isDefatomAgent(pkg.packageIdentifier));
    return selectedAgent ? [selectedAgent] : [];
  });
  const navigate = useNavigate();
  const auth = useAuth();

  const [loading, setLoading] = useState<boolean>(true);

  const [allUptoDateSelected, setAllUptoDateSelected] = useState<boolean>(false);
  const [allUpdatesSelected, setAllUpdatesSelected] = useState<boolean>(false);
  const [allInstallSelected, setAllInstallSelected] = useState<boolean>(false);
  const [paginationInfo, setPaginationInfo] = useState<PaginationInfo | null>(null);
  const { selectedTenantId } = useTenantSelection();
  const { canEdit } = usePermissions();
  const readOnly = !canEdit('groupPolicies');

  const syncSelectedPackages = (packages: SelectedPackage[]) => {
    setSelectedPackages(packages);

    setPolicyAutomation({
      ...PolicyAutomationData,
      policyContent: {
        ...PolicyAutomationData.policyContent,
        thirdPartyUpdates: {
          ...PolicyAutomationData.policyContent.thirdPartyUpdates,
          packages,
        },
      },
    });
  };

  const normalizeSelectedPackages = (packages: SelectedPackage[]) => {
    const activePackages = packages.filter((pkg) => pkg.isUpdateonly || pkg.isInstallonly);
    const hasNonAgentSelected = activePackages.some((pkg) => !isDefatomAgent(pkg.packageIdentifier));

    if (hasNonAgentSelected) {
      return activePackages.filter((pkg) => !isDefatomAgent(pkg.packageIdentifier));
    }

    const selectedAgent = activePackages.find((pkg) => isDefatomAgent(pkg.packageIdentifier));
    return selectedAgent ? [selectedAgent] : [];
  };

  const handleToggle = (index: number, field: 'isUpdateonly' | 'isInstallonly') => {
    const currentItem = data[index];
    const agentSelected = selectedPackages.some((pkg) => isDefatomAgent(pkg.packageIdentifier));
    const nonAgentSelected = selectedPackages.some((pkg) => !isDefatomAgent(pkg.packageIdentifier));
    const currentIsAgent = isDefatomAgent(currentItem.packageIdentifier);
    const selectionLocked = currentIsAgent ? nonAgentSelected : agentSelected;

    if (selectionLocked) {
      return;
    }

    let newData = [...data];
    newData[index] = {
      ...newData[index],
      [field]: !newData[index][field],
    };

    if (newData[index].isUpdateonly || newData[index].isInstallonly) {
      if (currentIsAgent) {
        newData = newData.map((item, itemIndex) =>
          itemIndex === index || isDefatomAgent(item.packageIdentifier) ? item : { ...item, isUpdateonly: false, isInstallonly: false },
        );
      } else {
        newData = newData.map((item) =>
          isDefatomAgent(item.packageIdentifier) ? { ...item, isUpdateonly: false, isInstallonly: false } : item,
        );
      }
    }

    setData(newData);

    if (newData[index].isUpdateonly || newData[index].isInstallonly) updatePolicyAutomation(newData, index);
    else {
      const updatedPackages = normalizeSelectedPackages(
        selectedPackages.filter((item) => item.packageIdentifier !== newData[index].packageIdentifier),
      );

      syncSelectedPackages(updatedPackages);
    }
  };

  const handleAllUpTodate = async () => {
    setAllUptoDateSelected((s) => !s);
    setLoading(true);
    try {
      const newData = [...data];
      newData.forEach((item) => {
        const itemIsAgent = isDefatomAgent(item.packageIdentifier);
        item.isUpdateonly = !allUptoDateSelected && !itemIsAgent;
        if (itemIsAgent) {
          item.isInstallonly = false;
        }
      });
      setData(newData);

      let updatedPackages = [...selectedPackages];

      if (!allUptoDateSelected) {
        const token = auth?.userData?.access_token;

        const { data } = await api.get(`${Project.api}patchreposervice/InstallerManifest/defatom-managed-packages`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (data?.isSuccess) {
          const pageData = Array.isArray(data.data) ? data.data : [];
          const filteredData = pageData.filter((item: SoftwareItem) => !isDefatomAgent(item.packageIdentifier));

          updatedPackages = updatedPackages.filter((pkg) => !isDefatomAgent(pkg.packageIdentifier));

          filteredData.forEach((item: SoftwareItem) => {
            const index = updatedPackages.findIndex((pkg) => pkg.packageIdentifier === item.packageIdentifier);
            const updated = {
              packageIdentifier: item.packageIdentifier,
              version: index >= 0 ? (updatedPackages[index].version?.toString() ?? '0') : '0',
              isUpdateonly: true,
              isInstallonly: index >= 0 ? updatedPackages[index].isInstallonly : false,
            };

            if (index === -1) {
              updatedPackages.push(updated);
            } else {
              updatedPackages[index] = updated;
            }
          });
        }
      } else {
        updatedPackages = updatedPackages.filter((item) => item.isInstallonly && !isDefatomAgent(item.packageIdentifier));
      }

      syncSelectedPackages(normalizeSelectedPackages(updatedPackages));
    } catch (err) {
      console.error('Error fetching uninstall list:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAllSelect = (field: 'isUpdateonly' | 'isInstallonly') => {
    let isSelected = false;
    if (field === 'isUpdateonly') {
      isSelected = !allUpdatesSelected;
      setAllUpdatesSelected((s) => !s);
    } else {
      isSelected = !allInstallSelected;
      setAllInstallSelected((s) => !s);
    }

    const newData = data.map((item) => {
      if (isSelected && isDefatomAgent(item.packageIdentifier)) {
        return { ...item, isUpdateonly: false, isInstallonly: false };
      }

      return { ...item, [field]: isSelected };
    });
    setData(newData);

    let updatedPackages = [...selectedPackages];

    if (isSelected) {
      updatedPackages = updatedPackages.filter((pkg) => !isDefatomAgent(pkg.packageIdentifier));
    }

    newData.forEach((item) => {
      if (item.isUpdateonly || item.isInstallonly) {
        const index = updatedPackages.findIndex((pkg) => pkg.packageIdentifier === item.packageIdentifier);
        const updated = {
          packageIdentifier: item.packageIdentifier,
          version: item.version?.toString() ?? '0',
          isUpdateonly: item.isUpdateonly,
          isInstallonly: item.isInstallonly,
        };

        if (index === -1) {
          updatedPackages.push(updated);
        } else {
          updatedPackages[index] = updated;
        }
      } else {
        const index = updatedPackages.findIndex((pkg) => pkg.packageIdentifier === item.packageIdentifier);
        if (index !== -1) updatedPackages.splice(index, 1);
      }
    });

    syncSelectedPackages(normalizeSelectedPackages(updatedPackages));
  };

  const getSoftwareDeploymentList = async (page = 1) => {
    setLoading(true);
    try {
      if (auth?.userData) {
        const token = auth.userData.access_token;
        const tenantId = selectedTenantId;

        const queryParams = new URLSearchParams({
          pageNumber: page.toString(),
          pageSize: ITEMS_PER_PAGE.toString(),
        });

        const { data } = await api.get(`${Project.api}patchreposervice/InstallerManifest/defatom-managed-packages?${queryParams}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (data?.isSuccess) {
          const pageData = Array.isArray(data.data) ? data.data : [];
          const filteredData = pageData;

          const syncedData = filteredData.map((item: SoftwareItem) => {
            const selected = selectedPackages.find((pkg) => pkg.packageIdentifier === item.packageIdentifier);
            if (selected) {
              return {
                ...item,
                isUpdateonly: selected.isUpdateonly,
                isInstallonly: selected.isInstallonly,
                version: selected.version ? parseInt(selected.version) : item.version,
              };
            }
            return item;
          });

          setData(syncedData);
          setPaginationInfo(data.pagination ?? null);
        }
      }
    } catch (err) {
      console.error('Error fetching uninstall list:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getSoftwareDeploymentList(currentPage);
    setAllUpdatesSelected(false);
    setAllInstallSelected(false);
  }, [auth, currentPage]);

  const handlePageChange = (page: number) => {
    if (page !== currentPage) setCurrentPage(page);
  };

  const handleVersionChange = (id: number, newVersion: number) => {
    const newData = [...data];
    const itemIndex = newData.findIndex((item) => item.id === id);
    if (itemIndex !== -1) {
      newData[itemIndex].version = newVersion;
      setData(newData);

      if (newData[itemIndex].isUpdateonly || newData[itemIndex].isInstallonly) updatePolicyAutomation(newData, itemIndex);
    }
  };

  const updatePolicyAutomation = (newData: SoftwareItem[], index: number) => {
    const selectedItem = newData[index];
    let updatedPackages = [...selectedPackages];

    if (isDefatomAgent(selectedItem.packageIdentifier)) {
      updatedPackages = updatedPackages.filter((pkg) => isDefatomAgent(pkg.packageIdentifier));
    } else {
      updatedPackages = updatedPackages.filter((pkg) => !isDefatomAgent(pkg.packageIdentifier));
    }

    const existsIndex = updatedPackages.findIndex((pkg) => pkg.packageIdentifier === selectedItem.packageIdentifier);
    const updated = {
      packageIdentifier: selectedItem.packageIdentifier,
      version: newData[index].version ? newData[index].version.toString() : '0',
      isUpdateonly: newData[index].isUpdateonly,
      isInstallonly: newData[index].isInstallonly,
    };

    if (existsIndex !== -1) {
      updatedPackages[existsIndex] = updated;
    } else {
      updatedPackages.push(updated);
    }

    syncSelectedPackages(normalizeSelectedPackages(updatedPackages));
  };

  const getCheckboxStyle = (isActive: boolean, isDisabled = false) => ({
    width: '16px',
    height: '16px',
    borderRadius: '2px',
    border: '2px solid #666',
    backgroundColor: isActive ? '#00FF00' : 'transparent',
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    opacity: isDisabled ? 0.4 : 1,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white">
        <LoaderCircle className="animate-spin w-10 h-10 text-white" />
      </div>
    );
  }

  const countSelected = selectedPackages.length;
  const isAgentSelected = selectedPackages.some((pkg) => isDefatomAgent(pkg.packageIdentifier));
  const isAnyNonAgentSelected = selectedPackages.some((pkg) => !isDefatomAgent(pkg.packageIdentifier));

  return (
    <div style={{ background: '#1e1e1e', borderRadius: '8px', overflow: 'hidden', marginTop: '20px' }}>
      {/* Header with Counter */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '1.5rem',
          borderBottom: '1px solid #2a2a2a',
          backgroundColor: '#161617',
        }}
      >
        <h3 style={{ margin: 0, color: '#ffffff', fontSize: '18px', fontWeight: '600' }}>Third-Party Software</h3>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            backgroundColor: 'rgba(239, 68, 68, 0.15)',
            padding: '8px 16px',
            borderRadius: '8px',
            border: '1px solid rgba(239, 68, 68, 0.3)',
          }}
        >
          <span style={{ color: '#9CA3AF', fontSize: '14px', fontWeight: '500' }}>Applications Selected:</span>
          <span style={{ color: '#EF4444', fontSize: '16px', fontWeight: '700' }}>{countSelected}</span>
        </div>
      </div>

      {/* Application UpToDate Option (aligned with table columns) */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '60px 60px 2fr 1fr',
          gap: '1rem',
          padding: '1.5rem',
          borderBottom: '1px solid #2a2a2a',
          backgroundColor: '#1e1e1e',
          alignItems: 'center',
        }}
      >
        {/* Align with Auto Update column */}
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <div
            onClick={() => !readOnly && handleAllUpTodate()}
            style={{
              ...getCheckboxStyle(allUptoDateSelected, readOnly),
              cursor: readOnly ? 'not-allowed' : 'pointer',
            }}
          />
        </div>
        <div />

        {/* Label aligned with "Name of the Software" column */}
        <div
          style={{
            color: '#ffffff',
            fontSize: '15px',
            fontWeight: '500',
            cursor: readOnly ? 'not-allowed' : 'pointer',
            userSelect: 'none',
          }}
          onClick={() => !readOnly && handleAllUpTodate()}
        >
          Keep All Applications Up to Date
        </div>

        {/* Empty to keep grid symmetry */}
        <div />
      </div>

      {/* Table Header */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '60px 60px 2fr 1fr',
          gap: '1rem',
          padding: '1.5rem',
          backgroundColor: '#1a1a1a',
          borderBottom: '2px solid #2a2a2a',
          color: '#9CA3AF',
          fontSize: '13px',
          fontWeight: '600',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
        }}
        className={Styles['tableHead']}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div
            onClick={() => !readOnly && handleAllSelect('isUpdateonly')}
            style={{ ...getCheckboxStyle(allUpdatesSelected, readOnly), cursor: readOnly ? 'not-allowed' : 'pointer' }}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div
            onClick={() => !readOnly && handleAllSelect('isInstallonly')}
            style={{ ...getCheckboxStyle(allInstallSelected, readOnly), cursor: readOnly ? 'not-allowed' : 'pointer' }}
          />
        </div>
        <div>Softwares</div>
        <div>Version</div>
      </div>

      {/* Column Labels */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '60px 60px 2fr 1fr',
          gap: '1rem',
          padding: '0.75rem 1.5rem',
          backgroundColor: '#161617',
          fontSize: '12px',
          color: '#666',
          borderBottom: '1px solid #2a2a2a',
        }}
      >
        <div style={{ textAlign: 'center' }}>Auto Update</div>
        <div style={{ textAlign: 'center' }}>Push Install</div>
        <div></div>
        <div></div>
      </div>

      {/* Table Rows */}
      {data.map((item, idx) => {
        const itemIsAgent = isDefatomAgent(item.packageIdentifier);
        const rowDisabled = readOnly || (itemIsAgent ? isAnyNonAgentSelected : isAgentSelected);

        return (
          <div
            key={item.id}
            style={{
              display: 'grid',
              gridTemplateColumns: '60px 60px 2fr 1fr',
              gap: '1rem',
              padding: '1.25rem 1.5rem',
              borderBottom: '1px solid #2a2a2a',
              background: idx % 2 === 0 ? '#161617' : '#1a1a1a',
              alignItems: 'center',
              transition: 'background-color 0.2s ease',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#202020')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = idx % 2 === 0 ? '#161617' : '#1a1a1a')}
          >
            {/* Auto Update Checkbox */}
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <div
                onClick={() => {
                  if (!rowDisabled) handleToggle(idx, 'isUpdateonly');
                }}
                style={{
                  ...getCheckboxStyle(item.isUpdateonly, rowDisabled),
                }}
              />
            </div>

            {/* Push Install Checkbox */}
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <div
                onClick={() => {
                  if (!rowDisabled) handleToggle(idx, 'isInstallonly');
                }}
                style={{
                  ...getCheckboxStyle(item.isInstallonly, rowDisabled),
                }}
              />
            </div>

            {/* Software Name */}
            <div style={{ color: '#ffffff', fontSize: '15px' }}>{item.packageName}</div>

            {/* Version Dropdown */}
            <select
              value={item.version}
              onChange={(e) => handleVersionChange(item.id, parseInt(e.target.value))}
              disabled={rowDisabled}
              style={{
                backgroundColor: '#2c2c2c',
                color: '#ffffff',
                borderRadius: '6px',
                padding: '8px 12px',
                border: '1px solid #3a3a3a',
                fontSize: '14px',
                appearance: 'none',
                cursor: rowDisabled ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%23999' d='M1 1l5 5 5-5'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 8px center',
                paddingRight: '28px',
                opacity: rowDisabled ? 0.5 : 1,
              }}
            >
              {[0, -1, -2, -3].map((value) => (
                <option key={value} value={value}>
                  {value === 0 ? 'Latest' : value}
                </option>
              ))}
            </select>
          </div>
        );
      })}

      {/* Pagination */}
      <div style={{ padding: '1.5rem' }}>
        <Pagination
          currentPage={currentPage}
          totalPages={paginationInfo?.totalPages ?? 1}
          loading={loading}
          onPageChange={handlePageChange}
        />
      </div>

      {/* Action Buttons */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-start',
          gap: '1rem',
          padding: '1.5rem',
          borderTop: '1px solid #2a2a2a',
          backgroundColor: '#161617',
        }}
      >
        <button
          style={{
            padding: '0.75rem 2rem',
            backgroundColor: 'transparent',
            color: '#fff',
            border: '1px solid #666',
            borderRadius: '25px',
            cursor: 'pointer',
            fontSize: '15px',
            fontWeight: '500',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#2a2a2a';
            e.currentTarget.style.borderColor = '#888';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.borderColor = '#666';
          }}
          onClick={() => navigate('/groupPolicy/CreatePolicy')}
        >
          Previous
        </button>
        <button
          style={{
            padding: '0.75rem 2rem',
            backgroundColor: '#DC2626',
            color: '#fff',
            border: 'none',
            borderRadius: '25px',
            cursor: 'pointer',
            fontSize: '15px',
            fontWeight: '600',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#B91C1C';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(220, 38, 38, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#DC2626';
            e.currentTarget.style.boxShadow = 'none';
          }}
          onClick={() => navigate('/groupPolicy/OS-Update')}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default ThirdPartyForm;
